// src/components/Authentication/Login.js

import React, { useState, useEffect, useContext } from "react";
import apis from "../../services/api";
import { AuthContext } from "../../contexts/AuthContext";
import Particles from "../Common/Particles";
import Input from "../Common/Input";
import { FaUser, FaLock, FaEye, FaEyeSlash, FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ToastContext } from "../../contexts/ToastContext";
import { mapAuthErrors } from "../../utils/errorMapping";

export default function Login() {
  const { handleLogin, handleRegister, isAuthenticated, userRole } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const [mode, setMode] = useState("login"); // 'login', 'register', 'forgotPassword'
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // For registration
  const [email, setEmail] = useState(""); // For registration
  const [showPassword, setShowPassword] = useState(false);
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchCaptcha = async () => {
    try {
      const base64Image = await apis.Security.getCaptchaImage();
      setCaptchaImage(`data:image/png;base64,${base64Image}`);
    } catch (err) {
      addToast("Error fetching CAPTCHA. Please try again.", "error");
    }
  };

  useEffect(() => {
    if (mode === "login") {
      fetchCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleRefreshCaptcha = () => {
    setCaptchaCode("");
    fetchCaptcha();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (mode === "forgotPassword") {
      // Handle forgot password submission
      try {
        const response = await apis.Security.forgotPassword({
          usernameOrEmail,
        });

        // Display success message using Toast
        addToast(response.message || "Please check your email for further instructions.", "success");

        // Optionally, reset the form or switch back to login view
        setMode("login");
        setUsernameOrEmail("");
      } catch (err) {
        const errorMsg = mapAuthErrors(err);
        addToast(errorMsg, "error");
      } finally {
        setLoading(false);
      }
    } else if (mode === "register") {
      // Handle registration submission
      try {
        if (password !== confirmPassword) {
          addToast("Passwords do not match.", "error");
          setLoading(false);
          return;
        }

        // Call handleRegister from AuthContext
        const registerResult = await handleRegister(usernameOrEmail, email, password);
        if (registerResult.success) {
          addToast(registerResult.message, "success");
          // Redirect to main page
          navigate("/", { replace: true });
        } else {
          addToast(registerResult.message, "error");
        }
      } catch (err) {
        const errorMsg = mapAuthErrors(err);
        addToast(errorMsg, "error");
      } finally {
        setLoading(false);
      }
    } else {
      // Handle login submission
      try {
        // Validate CAPTCHA first
        const captchaResponse = await apis.Security.validateCaptcha({
          CaptchaCode: captchaCode,
        });

        if (!captchaResponse.success) {
          const captchaErrorMsg = "Incorrect CAPTCHA. Please try again.";
          addToast(captchaErrorMsg, "error");
          handleRefreshCaptcha();
          setLoading(false);
          return;
        }

        // Proceed with login
        const loginResponse = await apis.Security.login({
          usernameOrEmail,
          password,
        });

        if (loginResponse.message === "Login successful") {
          await handleLogin(); // Await the authentication state update
          addToast("Login successful!", "success");
        } else {
          // Handle other success messages if any
          addToast(loginResponse.message || "Login successful!", "success");
          await handleLogin();
        }
      } catch (err) {

        // Use the error mapping utility to get a user-friendly message
        const errorMsg = mapAuthErrors(err);
        addToast(errorMsg, "error");

        // If the error is related to CAPTCHA, refresh it
        if (errorMsg.includes("CAPTCHA")) {
          handleRefreshCaptcha();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Redirect based on authentication and role
  useEffect(() => {
    if (isAuthenticated) {
      if (userRole === "Admin") {
        navigate("/role-selection", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userRole]);

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-b from-primary to-secondary overflow-hidden">
      {/* Light Overlay */}
      <div className="light-overlay"></div>

      {/* Particle Effects */}
      <Particles count={40} />

      {/* Login/Register/Forgot Password Card */}
      <div className="relative flex flex-col items-center bg-glassOverlay backdrop-blur-md p-8 rounded-lg shadow-lg w-80 z-10">
        <h2 className="text-3xl font-semibold text-accent mb-6 text-center">
          {mode === "login" && "Welcome Back to MF"}
          {mode === "register" && "Create an Account"}
          {mode === "forgotPassword" && "Reset Your Password"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6 w-full" noValidate>
          {/* Username Field */}
          <Input
            type="text"
            placeholder="Username or Email"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            icon={<FaUser />}
            required
            aria-label="Username or Email"
          />

          {/* Email Field - Only in Register Mode */}
          {mode === "register" && (
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={<FaEnvelope />}
              required
              aria-label="Email"
            />
          )}

          {/* Password Field - Not in Forgot Password Mode */}
          {mode !== "forgotPassword" && (
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon={<FaLock />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-gray-500 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              }
              required={mode !== "register"} // Required in login mode
              aria-label="Password"
            />
          )}

          {/* Confirm Password Field - Only in Register Mode */}
          {mode === "register" && (
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              icon={<FaLock />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-gray-500 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              }
              required={mode === "register"}
              aria-label="Confirm Password"
            />
          )}

          {/* CAPTCHA - Only in Login Mode */}
          {mode === "login" && captchaImage && (
            <div className="flex flex-col items-center">
              <img
                src={captchaImage}
                alt="CAPTCHA"
                className="mb-4 w-32 h-12 object-contain rounded-md"
              />
              <button
                type="button"
                onClick={handleRefreshCaptcha}
                className="text-sm text-accent underline mb-2 hover:text-accentBlue transition-colors duration-200"
                aria-label="Refresh CAPTCHA"
              >
                Refresh CAPTCHA
              </button>
              <Input
                type="text"
                placeholder="Enter CAPTCHA Code"
                value={captchaCode}
                onChange={(e) => setCaptchaCode(e.target.value)}
                required={mode === "login"}
                aria-label="CAPTCHA Code"
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`${
              loading
                ? "bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg cursor-not-allowed"
                : "bg-accentBlue hover:bg-accent hover:bg-opacity-90"
            } text-white font-semibold py-3 px-8 rounded-lg w-full transition-all duration-300 flex items-center justify-center`}
            aria-label={
              mode === "login"
                ? "Login"
                : mode === "register"
                ? "Register"
                : "Reset Password"
            }
          >
            {loading ? (
              <svg
                className="animate-spin h-5 w-5 mr-3 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            ) : mode === "forgotPassword" ? (
              "Reset Password"
            ) : mode === "register" ? (
              "Register"
            ) : (
              "Login"
            )}
          </button>

          {/* Toggle between Login, Register, and Forgot Password */}
          <div className="text-center">
            {mode === "login" && (
              <>
                <p className="text-sm">
                  Forgot your password?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("forgotPassword")}
                    className="text-accent underline hover:text-accentBlue transition-colors duration-200"
                  >
                    Reset here
                  </button>
                </p>
                <p className="text-sm mt-2">
                  Don't have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("register")}
                    className="text-accent underline hover:text-accentBlue transition-colors duration-200"
                  >
                    Register
                  </button>
                </p>
              </>
            )}
            {mode === "register" && (
              <p className="text-sm">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-accent underline hover:text-accentBlue transition-colors duration-200"
                >
                  Login
                </button>
              </p>
            )}
            {mode === "forgotPassword" && (
              <p className="text-sm">
                Remembered your password?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-accent underline hover:text-accentBlue transition-colors duration-200"
                >
                  Login
                </button>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
